import { useMemo, useCallback } from "react";
import { GPEventResponse } from "../../../types";
import {
	InsightEventGroupEnum,
	InsightCategoryEnum,
	isOneTeamTagging,
	ONE_TEAM_TAGGING_OMITTED_INSIGHT_EVENT_TYPES,
} from "../constants";
import { groupEventsByCategories } from "../functions";
import eventFilterClass from "../../Video/Filter/eventFilter";

interface UseEventGroupingProps {
	events?: GPEventResponse[];
	eventFilter: eventFilterClass | null;
	periods: string[];
	team: number | null;
	selectedPlayer: any;
	activeCategoryTab: InsightEventGroupEnum;
	teamTagged?: string | null;
}

const omitOneTeamTaggingEvents = <T extends Record<string, unknown>>(
	categoryEvents: T,
	teamTagged?: string | null,
): T => {
	if (isOneTeamTagging(teamTagged) === false) {
		return categoryEvents;
	}

	const filteredEntries = Object.entries(categoryEvents).filter(
		([eventType]) => ONE_TEAM_TAGGING_OMITTED_INSIGHT_EVENT_TYPES.has(eventType) === false,
	);

	return Object.fromEntries(filteredEntries) as T;
};

export const useEventGrouping = ({
	events,
	eventFilter,
	periods,
	team,
	selectedPlayer,
	activeCategoryTab,
	teamTagged,
}: UseEventGroupingProps) => {
	// Memoize the full grouped events - React's useMemo handles caching
	const allGroupedEvents = useMemo(() => {
		if (!events || events?.length === 0 || !eventFilter) {
			return null;
		}

		const grouped = groupEventsByCategories(events, eventFilter, periods);
		if (isOneTeamTagging(teamTagged) === false) {
			return grouped;
		}

		return {
			[InsightCategoryEnum.Offensive]: omitOneTeamTaggingEvents(
				grouped[InsightCategoryEnum.Offensive],
				teamTagged,
			),
			[InsightCategoryEnum.Defensive]: omitOneTeamTaggingEvents(
				grouped[InsightCategoryEnum.Defensive],
				teamTagged,
			),
			[InsightCategoryEnum.Distribution]: grouped[InsightCategoryEnum.Distribution],
			[InsightCategoryEnum.Goalkeeper]: grouped[InsightCategoryEnum.Goalkeeper],
		};
	}, [events, eventFilter, periods, teamTagged]);

	// Extract events for a specific category from the cached grouped events
	const getEventsForCategory = useCallback(
		(category: InsightEventGroupEnum) => {
			if (!allGroupedEvents) {
				return {};
			}
			return allGroupedEvents[category as unknown as InsightCategoryEnum] || {};
		},
		[allGroupedEvents]
	);

	// Get events only for the active category - this is now instant (just reading from cache)
	const activeCategoryEvents = useMemo(() => {
		if (!allGroupedEvents) return {};
		return allGroupedEvents[activeCategoryTab as unknown as InsightCategoryEnum] || {};
	}, [activeCategoryTab, allGroupedEvents]);

	// Build the structure that widgets expect
	const activeGroupedEvents = useMemo(() => {
		if (!activeCategoryEvents) return {};
		return {
			[activeCategoryTab]: activeCategoryEvents,
		};
	}, [activeCategoryTab, activeCategoryEvents]);

	return {
		allGroupedEvents,
		getEventsForCategory,
		activeCategoryEvents,
		activeGroupedEvents,
	};
};
